import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { useDebounce } from "./useDebounce";
import {
  ExternalControl,
  ListExternalControlsResponseSchema,
} from "../models/audit/externalControl";
import { toast } from "sonner";

const PAGE_SIZE = 10;

interface CachedPage {
  controls: ExternalControl[];
  total: number;
  source: string;
}

export function useExternalControlSearch(
  search: string,
  page: number,
  enabled: boolean,
) {
  const debouncedSearch = useDebounce(search, 300);
  const [controls, setControls] = useState<ExternalControl[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<string>("");

  const cacheRef = useRef<Map<string, CachedPage>>(new Map());
  const prevSearchRef = useRef(debouncedSearch);

  if (debouncedSearch !== prevSearchRef.current) {
    cacheRef.current.clear();
    prevSearchRef.current = debouncedSearch;
  }

  if (!enabled) {
    cacheRef.current.clear();
  }

  useEffect(() => {
    if (!enabled) {
      setControls([]);
      setTotal(0);
      return;
    }

    const cacheKey = `${debouncedSearch}:${page}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setControls(cached.controls);
      setTotal(cached.total);
      setSource(cached.source);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchControls = async () => {
      setLoading(true);
      try {
        const query: Record<string, string> = {
          offset: String(page * PAGE_SIZE),
          limit: String(PAGE_SIZE),
        };
        if (debouncedSearch) {
          query.search = debouncedSearch;
        }

        const response = await fetchFastApi({
          method: "GET",
          path: "/controls/external/controls",
          query,
        });

        if (cancelled) return;

        const parsed = ListExternalControlsResponseSchema.parse(response);
        const entry: CachedPage = {
          controls: parsed.controls,
          total: parsed.total,
          source: parsed.source,
        };
        cacheRef.current.set(cacheKey, entry);

        setControls(parsed.controls);
        setTotal(parsed.total);
        setSource(parsed.source);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to search external controls:", error);
        toast.error("Failed to search external controls");
        setControls([]);
        setTotal(0);
        setSource("");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchControls();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, page, enabled]);

  return { controls, total, loading, source };
}

export function useExternalRef(controlId: string) {
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const linkControl = useCallback(
    async (externalRef: string): Promise<boolean> => {
      setLinking(true);
      try {
        await fetchFastApi({
          method: "PUT",
          path: `/controls/external/control/${controlId}/external-ref`,
          body: { externalRef },
        });
        toast.success("Control linked successfully");
        return true;
      } catch (error) {
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to link control");
        }
        return false;
      } finally {
        setLinking(false);
      }
    },
    [controlId],
  );

  const unlinkControl = useCallback(async (): Promise<boolean> => {
    setUnlinking(true);
    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/controls/external/control/${controlId}/external-ref`,
      });
      toast.success("Control unlinked successfully");
      return true;
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.getUserMessage());
      } else {
        toast.error("Failed to unlink control");
      }
      return false;
    } finally {
      setUnlinking(false);
    }
  }, [controlId]);

  return { linkControl, unlinkControl, linking, unlinking };
}
